import React from 'react';
import { useSelector } from 'react-redux';
import {
    Grid, Text, Box, Image, Flex, Divider
} from 'components/ui';
import { wrapFunctionalComponent } from 'utils/framework';
import localeUtils from 'utils/LanguageLocale';
import { isGenAIPRSEnabledSelector } from 'ai/selectors/killSwitches/isGenAIPRSEnabledSelector';
import HistogramChart from 'components/ProductPage/RatingsAndReviews/HistogramChart/HistogramChart';
import ProsAndCons from 'components/ProductPage/RatingsAndReviews/ProsAndCons';
import PRSSummary from 'ai/components/PRSSummary';
import ReviewLegalText from 'components/ProductPage/RatingsAndReviews/Legal/ReviewLegalText';
import TrustMark from 'components/ProductPage/RatingsAndReviews/TrustMark';
import mediaUtils from 'utils/Media';
import ReviewsStatsEN from 'components/ProductPage/RatingsAndReviews/ReviewsStats/locales/ReviewsStats_en_US.js';
import ReviewsStatsFR from 'components/ProductPage/RatingsAndReviews/ReviewsStats/locales/ReviewsStats_fr_CA.js';

const { Media } = mediaUtils;
const percentage = (maxValue, actualValue) => Math.min(100, Math.round((100 * actualValue) / maxValue));

// eslint-disable-next-line complexity
function ReviewsStats(props) {
    const {
        sentiments, reviewStatistics, totalReviewCount, showProsAndCons, isDropDownView
    } = props;
    const isGenAIPRSEnabled = useSelector(isGenAIPRSEnabledSelector);
    const getText = localeUtils.getLocalisedDataFromResource(ReviewsStatsEN, ReviewsStatsFR);

    const prosAndConsEnabled =
        sentiments && sentiments.length > 0 && Sephora.configurationSettings.prosAndCons && showProsAndCons && !isGenAIPRSEnabled;
    const isBVTrustMarkEnabled = Sephora.configurationSettings.isBVTrustMarkEnabled;
    const reviewsLegalComponent = isBVTrustMarkEnabled ? <TrustMark /> : <ReviewLegalText />;

    if (!reviewStatistics) {
        return (
            <>
                {reviewsLegalComponent}
                {isGenAIPRSEnabled ? (
                    <PRSSummary />
                ) : prosAndConsEnabled ? (
                    <>
                        <Divider marginY={4} />
                        <ProsAndCons />
                    </>
                ) : null}
            </>
        );
    }

    const { ratingDistribution, recommendedCount = 0, averageOverallRating = 0 } = reviewStatistics;

    const recommendedPercentage = percentage(totalReviewCount, recommendedCount);
    const reviewsText = isBVTrustMarkEnabled ? `${getText('reviews')}*` : getText('reviews');
    const formattedCount = totalReviewCount.toLocaleString(localeUtils.isFRCanada() ? 'fr-CA' : 'en-US');

    return (
        <Box marginTop={[4, null, 5]}>
            <Grid
                columns={[null, 'minmax(min-content, 420px) auto minmax(360px, 1fr)']}
                gap={[null, 5]}
                marginLeft={!prosAndConsEnabled && !isGenAIPRSEnabled && !isDropDownView && [null, null, null, 212]}
                maxWidth={1072}
            >
                <Box {...(isDropDownView && { width: 375 })}>
                    {!isDropDownView && (
                        <Text
                            is='h3'
                            fontWeight={(prosAndConsEnabled || isGenAIPRSEnabled) && 'bold'}
                            marginBottom={1}
                            display={['none', 'block']}
                            children={getText('summary')}
                        />
                    )}
                    <Grid
                        columns='minmax(auto, 265px) minmax(min-content, auto)'
                        alignItems='center'
                    >
                        <div>
                            <HistogramChart
                                ratingDistribution={ratingDistribution}
                                totalReviewCount={totalReviewCount}
                                percentage={percentage}
                                disableInteraction={isDropDownView}
                                isPRSEnabled={isGenAIPRSEnabled}
                                productId={props.productId}
                            />
                        </div>
                        <Box
                            textAlign='center'
                            lineHeight='none'
                        >
                            <Flex
                                justifyContent='center'
                                alignItems='center'
                            >
                                <Text
                                    fontSize={['xl', '2xl']}
                                    children={parseFloat(averageOverallRating).toFixed(1)}
                                />
                                <Image
                                    marginLeft={2}
                                    src='/img/ufe/icons/star.svg'
                                    size={[16, 20]}
                                />
                            </Flex>
                            <Text
                                marginTop={1}
                                display='block'
                                fontSize='sm'
                            >
                                {formattedCount} {reviewsText}
                            </Text>
                            {recommendedPercentage >= 80 && (
                                <Box marginTop={[5, 6]}>
                                    <Text
                                        fontSize={['xl', '2xl']}
                                        children={`${recommendedPercentage > 100 ? 100 : recommendedPercentage}%`}
                                    />
                                    <Text
                                        marginTop={1}
                                        display='block'
                                        fontSize='sm'
                                    >
                                        {getText('recommended')}
                                    </Text>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Box>
                {!isDropDownView && (
                    <>
                        <Box
                            display={['none', 'block']}
                            borderLeft={1}
                            borderColor='divider'
                            marginRight={(prosAndConsEnabled || isGenAIPRSEnabled) && [null, null, 6]}
                        />

                        {isGenAIPRSEnabled ? (
                            <>
                                <Media at='xs'>{reviewsLegalComponent}</Media>
                                <PRSSummary />
                            </>
                        ) : prosAndConsEnabled ? (
                            <>
                                <Media at='xs'>
                                    {reviewsLegalComponent}
                                    <Divider marginY={4} />
                                </Media>
                                <ProsAndCons />
                            </>
                        ) : (
                            <>{reviewsLegalComponent}</>
                        )}
                    </>
                )}
            </Grid>
            {(prosAndConsEnabled || isGenAIPRSEnabled) && <Media greaterThan='xs'>{reviewsLegalComponent}</Media>}
        </Box>
    );
}

export default wrapFunctionalComponent(ReviewsStats, 'ReviewsStats');
